import { CalendarWeeklyGoals } from '../../components/calendar/CalendarWeeklyGoals';
import { CalendarPlanSummary } from '../../components/calendar/CalendarPlanSummary';
import { getWeekStripDays } from '../../utils/calendarPickerLocale';
import { MobileHeaderIconButton } from '../../shell/MobileHeaderIconButton';
import { createPortal } from 'react-dom';
import './calendar-toolbar.css';
import './calendar-minimal.css';
import { formatCalendarToolbarMonthYear } from '../../utils/calendarPickerLocale';
import { displayDigits } from '../../utils/displayDigits';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useLocation, useNavigate } from 'react-router-dom';

import { NmEmptyState } from '../../components/NmEmptyState';

import { NmLoadingOverlay } from '../../components/NmLoadingOverlay';

import { CalendarUploadOverlay } from '../../components/CalendarUploadOverlay';

import { NmDatePickerModal } from '../../components/NmDatePickerModal';

import { IoCloudUploadOutline } from 'react-icons/io5';

import { CalendarStatusLegend } from '../../components/CalendarStatusLegend';
import { NmWeekStrip } from '../../components/NmWeekStrip';
import { ActionButton, ActionIconButton, ActionToggleGroup } from '../../components/ui/ActionButton';

import { usePageRefresh } from '../../context/PageRefreshContext';
import { useAthleteContext } from '../../context/AthleteContext';
import { useToast, toastErrorMessage } from '../../context/ToastContext';

import { useCalendarData } from '../../hooks/useCalendarData';

import { useCalendarDayFileDrop } from '../../hooks/useCalendarDayFileDrop';
import { toastCalendarFileDropResult, uploadCalendarDayFiles, hitTestCalendarDate, type CalendarFileDropResult, type CalendarUploadProgress } from '../../utils/calendarFileDrop';
import {
  addJalaliDays,
  isoToJalaliKey,
  jalaliKeyToIso,
  jalaliDateKey,
  parseJalaliDateKey,
  todayJalali,
} from '../../utils/jalali';

import type { CalendarWorkout } from '../../services/api';

import type { CalendarGridItem } from '../../utils/calendarGrid';

import { CalendarAnimatedBg } from './CalendarAnimatedBg';
import { CalendarDayDetail } from './CalendarDayDetail';
import { CalendarReadinessSummary } from './CalendarReadinessSummary';
import { CalendarWeekDetail } from './CalendarWeekDetail';
import { VO2CalendarGrid } from './VO2CalendarGrid';
import { WorkoutDetailPanel } from '../../pages/WorkoutDetailPanel';



function buildDayMap(items: CalendarGridItem[]): Map<string, CalendarGridItem> {

  const map = new Map<string, CalendarGridItem>();

  for (const item of items) {

    if (item.day === '-' || item.day === '') continue;

    map.set(jalaliDateKey(item.year, item.month, Number(item.day)), item);

  }

  return map;

}



/** Weekly strip + selected-day workouts (VO2 DS Buttons & Calendar). */

export function CalendarPage() {

  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { effectiveUserId, coachMenuLocked } = useAthleteContext();

  const { items, loading, reload, ensureMonth } = useCalendarData(effectiveUserId);

  usePageRefresh(reload, [reload]);

  useEffect(() => {
    const state = location.state as { toast?: string; refreshCalendar?: boolean; openDatePicker?: boolean } | null;
    if (!state?.toast && !state?.refreshCalendar && !state?.openDatePicker) return;

    if (state.toast) showToast(state.toast);
    if (state.refreshCalendar) void reload();
    if (state.openDatePicker) setDatePickerOpen(true);

    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, reload, showToast]);

  const fileRef = useRef<HTMLInputElement>(null);



  const today = todayJalali();

  const todayDateKey = jalaliDateKey(today.year, today.month, today.dat);



  const [selectedDateKey, setSelectedDateKey] = useState<string>(todayDateKey);

  const [weekAnchor, setWeekAnchor] = useState(() => ({

    year: today.year,

    month: today.month,

    day: today.dat,

  }));

  const [recordSection, setRecordSection] = useState<'records' | undefined>();
  const [recordAthleteId, setRecordAthleteId] = useState<number | undefined>();
  const [detailWorkoutId, setDetailWorkoutId] = useState<number | null>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<CalendarUploadProgress | null>(null);

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [monthExpanded, setMonthExpanded] = useState(false);
  const [showFullWeek, setShowFullWeek] = useState(false);
  const [hasAnnualPlan, setHasAnnualPlan] = useState(false);
  const monthLabel = formatCalendarToolbarMonthYear(
    weekAnchor.year, weekAnchor.month, weekAnchor.day, i18n.language,
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const workoutId = Number(params.get('workoutId') ?? params.get('workout'));
    const isoDate = params.get('date');
    if (isoDate && /^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
      const dateKey = isoToJalaliKey(isoDate);
      const parsed = parseJalaliDateKey(dateKey);
      setSelectedDateKey(dateKey);
      if (parsed) { setWeekAnchor(parsed); void ensureMonth(parsed.year, parsed.month); }
    }
    if (!Number.isFinite(workoutId) || workoutId <= 0) return;
    setRecordSection(params.get('section') === 'records' ? 'records' : undefined);
    const athleteId = Number(params.get('athleteId'));
    setRecordAthleteId(athleteId > 0 ? athleteId : undefined);
    setDetailWorkoutId(workoutId);
    // Keep the target in the URL until close: a PWA worker update may reload this page.
  }, [location.search, navigate]);



  const dayByKey = useMemo(() => buildDayMap(items), [items]);

  const selectedItem = dayByKey.get(selectedDateKey) ?? null;




  const syncWeekAnchor = useCallback((dateKey: string) => {

    const parsed = parseJalaliDateKey(dateKey);

    if (parsed) setWeekAnchor(parsed);

  }, []);



  const onSelectDay = useCallback(

    (dateKey: string) => {

      setSelectedDateKey(dateKey);

      syncWeekAnchor(dateKey);

    },

    [syncWeekAnchor],

  );



  const onPrevWeek = useCallback(() => {
    const parsed = parseJalaliDateKey(selectedDateKey) ?? weekAnchor;
    const next = addJalaliDays(parsed.year, parsed.month, parsed.day, -7);
    const key = jalaliDateKey(next.year, next.month, next.dat);
    setWeekAnchor({ year: next.year, month: next.month, day: next.dat });
    setSelectedDateKey(key);
  }, [selectedDateKey, weekAnchor]);

  const onNextWeek = useCallback(() => {
    const parsed = parseJalaliDateKey(selectedDateKey) ?? weekAnchor;
    const next = addJalaliDays(parsed.year, parsed.month, parsed.day, 7);
    const key = jalaliDateKey(next.year, next.month, next.dat);
    setWeekAnchor({ year: next.year, month: next.month, day: next.dat });
    setSelectedDateKey(key);
  }, [selectedDateKey, weekAnchor]);



  const onToday = useCallback(() => {

    onSelectDay(todayDateKey);

  }, [onSelectDay, todayDateKey]);

  const onGridDayPress = useCallback(
    (_globalIdx: number, dateKey?: string | null) => {
      if (dateKey) onSelectDay(dateKey);
    },
    [onSelectDay],
  );

  const onExpandMonth = useCallback(async () => {
    const anchor = parseJalaliDateKey(selectedDateKey) ?? weekAnchor;
    setMonthExpanded(true);
    await ensureMonth(anchor.year, anchor.month, { force: true });
  }, [ensureMonth, selectedDateKey, weekAnchor]);

  const onCollapseMonth = useCallback(() => {
    setMonthExpanded(false);
  }, []);

  const onGridMonthNav = useCallback(
    (year: number, month: number) => {
      void ensureMonth(year, month);
    },
    [ensureMonth],
  );

  useEffect(() => {
    if (!monthExpanded) return;
    const anchor = parseJalaliDateKey(selectedDateKey) ?? weekAnchor;
    void ensureMonth(anchor.year, anchor.month);
  }, [ensureMonth, monthExpanded, selectedDateKey, weekAnchor]);

  /** Remount month grid when opening a different Jalali month (correct initial viewMonth). */
  const monthGridKey = useMemo(() => {
    const anchor = parseJalaliDateKey(selectedDateKey) ?? weekAnchor;
    return `${anchor.year}-${anchor.month}`;
  }, [selectedDateKey, weekAnchor]);






  const [headerActions, setHeaderActions] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHeaderActions(document.getElementById('calendar-header-actions'));
  }, []);

  const handleUpload = useCallback(() => {

    fileRef.current?.click();

  }, []);



  const focusCalendarAfterUpload = useCallback(

    async (result: CalendarFileDropResult) => {

      await reload();

      if (!result.focusGregorianDate) return;

      const key = isoToJalaliKey(result.focusGregorianDate);

      if (!key) return;

      setSelectedDateKey(key);

      syncWeekAnchor(key);

      const parsed = parseJalaliDateKey(key);

      if (parsed) await ensureMonth(parsed.year, parsed.month);

    },

    [ensureMonth, reload, syncWeekAnchor],

  );

  const focusCalendarOnIsoDate = useCallback(
    async (iso: string) => {
      const key = isoToJalaliKey(iso);
      if (!key) return;
      onSelectDay(key);
      const parsed = parseJalaliDateKey(key);
      if (parsed) await ensureMonth(parsed.year, parsed.month);
    },
    [ensureMonth, onSelectDay],
  );



  const onFiles = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length || !effectiveUserId) return;

      setUploading(true);
      setUploadProgress({ phase: 'uploading', uploadPercent: 0 });
      try {
        const result = await uploadCalendarDayFiles(Array.from(files), {
          userId: effectiveUserId,
          locale: i18n.language?.split('-')[0],
          onProgress: setUploadProgress,
        });

        toastCalendarFileDropResult(result, t, message => showToast(message));

        const hadSuccess =
          result.activityImportedCount +
            result.healthSavedCount +
            result.healthPartialCount >
          0;
        if (hadSuccess) {
          await focusCalendarAfterUpload(result);
        }
      } catch (err) {
        const msg =
          err instanceof Error && err.message === 'too_many_activity_files'
            ? t('calendar.toast.tooManyFiles')
            : toastErrorMessage(err, t('calendar.toast.importFailed'));
        showToast(msg);
      } finally {
        setUploading(false);
        setUploadProgress(null);
        e.target.value = '';
      }
    },
    [effectiveUserId, focusCalendarAfterUpload, i18n.language, showToast, t],
  );

  const globalFileDrop = useCalendarDayFileDrop({
    userId: effectiveUserId,
    onComplete: focusCalendarAfterUpload,
  });



  if (coachMenuLocked) {

    return <NmEmptyState>{t('coach.addAthleteFirst')}</NmEmptyState>;

  }



  return (

    <div
      className={`calendar-page calendar-page--weekly calendar-page--animated-bg${
        monthExpanded ? ' calendar-page--month-expanded' : ''
      }${globalFileDrop.isFileDragOver ? ' calendar-page--file-drag' : ''}`}
      onDragOver={globalFileDrop.onDragOver}
      onDragLeave={globalFileDrop.onDragLeave}
      onDrop={(e) => {
        // Prefer capturing overlay when visible; this is a backup for first-frame drops.
        if (globalFileDrop.isFileDragOver) return;
        globalFileDrop.onDrop(e);
      }}
    >
      {globalFileDrop.isFileDragOver && (
        <div
          className="calendar-page__global-drop-hint"
          aria-hidden
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              globalFileDrop.onDragLeave(e);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const date = hitTestCalendarDate(e.clientX, e.clientY, e.currentTarget);
            const files = Array.from(e.dataTransfer.files ?? []);
            if (files.length) void globalFileDrop.processFiles(files, date);
          }}
        >
          {t('calendar.dropHint.fileAuto')}
        </div>
      )}
      <CalendarAnimatedBg />

      {headerActions && createPortal(
        <MobileHeaderIconButton onClick={handleUpload} disabled={uploading}
          label={t('calendar.action.uploadFile')} icon={<IoCloudUploadOutline aria-hidden />} />, headerActions
      )}
      {!hasAnnualPlan && selectedDateKey === todayDateKey && selectedItem?.extra?.today && (
        <div className="calendar-page__readiness-top">
          <CalendarReadinessSummary today={selectedItem.extra.today as unknown as Record<string, unknown>} />
        </div>
      )}
      <CalendarPlanSummary onOpenPlan={() => navigate('/atp')} planPath="/atp" weekly onPlanChange={setHasAnnualPlan} userId={effectiveUserId}
        dates={getWeekStripDays(weekAnchor.year, weekAnchor.month, weekAnchor.day, i18n.language).map(d => jalaliKeyToIso(d.dateKey))}
        days={items.flatMap(item => item.extra ? [item.extra] : [])} refreshToken={loading} loading={loading} />
      <CalendarWeeklyGoals userId={effectiveUserId} refreshToken={loading}
        dates={getWeekStripDays(weekAnchor.year, weekAnchor.month, weekAnchor.day, i18n.language).map(d => jalaliKeyToIso(d.dateKey))} />
      <div className="calendar-page__toolbar calendar-controls">
        <div className="calendar-controls__views" role="group" aria-label={t('calendar.mobileViews.label')}>
          <ActionButton type="button" className="calendar-controls__date-tab"
            aria-label={`${t('calendar.datePicker.title')} — ${displayDigits(`${monthLabel.monthLine} ${monthLabel.yearLine}`, i18n.language)}`}
            aria-haspopup="dialog" aria-expanded={datePickerOpen}
            onClick={() => setDatePickerOpen(true)}>
            <span>{monthLabel.monthLine}</span>
            <small>{displayDigits(monthLabel.yearLine, i18n.language)}</small>
            <span className="calendar-controls__date-chevron" aria-hidden="true">⌄</span>
          </ActionButton>
          <ActionToggleGroup
            className="calendar-controls__tabs"
            label={t('calendar.mobileViews.label')}
            value={monthExpanded ? 'month' : showFullWeek ? 'list' : 'week'}
            options={[
              { value: 'today', label: t('calendar.today', 'Today') },
              { value: 'week', label: t('calendar.mobileViews.week') },
              { value: 'month', label: t('calendar.mobileViews.month') },
              { value: 'list', label: t('calendar.mobileViews.list') },
            ]}
            onChange={value => {
              if (value === 'today') { onToday(); return; }
              setShowFullWeek(value === 'list');
              if (value === 'month') void onExpandMonth();
              else onCollapseMonth();
            }}
          />
        </div>
      </div>

      <div className="calendar-page__calendar-shell">

        {!monthExpanded ? (
          <div className="calendar-week-card">
            <NmWeekStrip
              weekAnchor={weekAnchor}
              selectedDateKey={selectedDateKey}
              dayByKey={dayByKey}
              onSelect={onSelectDay}
              onPrevWeek={onPrevWeek}
              onNextWeek={onNextWeek}
            />
          </div>
        ) : (
          <div className="calendar-month-card">
            <div className="calendar-page__grid-shell calendar-page__grid-shell--expanded">
              <VO2CalendarGrid
                key={monthGridKey}
                items={items}
                selectedDateKey={selectedDateKey}
                onDayPress={onGridDayPress}
                onPreviousMonth={onGridMonthNav}
                onNextMonth={onGridMonthNav}
              />
            </div>
          </div>
        )}
        <CalendarStatusLegend />
      </div>

      <div className="calendar-page__detail">
        {showFullWeek ? (
          <CalendarWeekDetail
            weekAnchor={weekAnchor}
            selectedDateKey={selectedDateKey}
            dayByKey={dayByKey}
            userId={effectiveUserId}
            onReload={reload}
            onUploadComplete={focusCalendarAfterUpload}
            onMetricMoved={focusCalendarOnIsoDate}
            onWorkoutPress={(w: CalendarWorkout) => setDetailWorkoutId(w.id)}
          />
        ) : (
          <CalendarDayDetail
            item={selectedItem}
            dateKey={selectedDateKey}
            userId={effectiveUserId}
            onReload={reload}
            onUploadComplete={focusCalendarAfterUpload}
            onMetricMoved={focusCalendarOnIsoDate}
            onWorkoutPress={(w: CalendarWorkout) => setDetailWorkoutId(w.id)}
          />
        )}
      </div>



      <input
        ref={fileRef}
        type="file"
        accept=".fit,.FIT,.wse,.WSE,.tcx,.TCX,.pdf,.PDF,image/*"
        multiple
        hidden
        onChange={onFiles}
      />



      {(uploadProgress || globalFileDrop.uploadProgress) && (
        <CalendarUploadOverlay
          progress={(uploadProgress ?? globalFileDrop.uploadProgress)!}
        />
      )}

      {loading && !uploadProgress && !globalFileDrop.uploadProgress && <NmLoadingOverlay />}



      <NmDatePickerModal
        open={datePickerOpen}
        value={selectedDateKey}
        onClose={() => setDatePickerOpen(false)}
        onConfirm={(key) => {
          onSelectDay(key);
          const parsed = parseJalaliDateKey(key);
          if (parsed) void ensureMonth(parsed.year, parsed.month);
        }}
      />



      {detailWorkoutId != null && effectiveUserId != null && (

        <WorkoutDetailPanel

          workoutId={detailWorkoutId}
          initialSection={recordSection}

          userId={recordAthleteId ?? effectiveUserId}

          onClose={() => { setDetailWorkoutId(null); setRecordSection(undefined); setRecordAthleteId(undefined); navigate('/calendar', { replace: true }); }}

          onDeleted={() => {
            setDetailWorkoutId(null);
            setRecordSection(undefined);
            setRecordAthleteId(undefined);
            navigate('/calendar', { replace: true });
            void reload();
          }}

          onUpdated={() => void reload()}

        />

      )}

    </div>

  );

}
